import { defineStore } from 'pinia';
import request from '@/utils/request';
import useUserStore from '@/store/user';

/** 0 = Balanced (Commentator), 1 = Stadium (requires subscription) */
export type AudioPreferenceValue = 0 | 1;

const useAudioPreferenceStore = defineStore('audioPreference', {
  state: () => ({
    /** 0 = Balanced, 1 = Stadium. null = not loaded */
    preference: null as AudioPreferenceValue | null,
    _fetchPromise: null as Promise<AudioPreferenceValue | null> | null,
  }),
  getters: {
    isStadium: state => state.preference === 1,
    isBalanced: state => state.preference === 0,
  },
  actions: {
    async ensureLoaded(): Promise<AudioPreferenceValue | null> {
      const userStore = useUserStore();
      if (!userStore.isLogin) {
        this.preference = null;
        return null;
      }
      if (this.preference !== null) return this.preference;
      if (this._fetchPromise) {
        await this._fetchPromise;
        return this.preference;
      }
      this._fetchPromise = this.fetchPreference();
      try {
        const val = await this._fetchPromise;
        this.preference = val;
        return val;
      } finally {
        this._fetchPromise = null;
      }
    },
    async fetchPreference(): Promise<AudioPreferenceValue | null> {
      try {
        const res: any = await request.get('/user/userInfo/c/settings/get.json');
        const val = res?.data?.audio_preference ?? res?.data?.audioPreference;
        if (val === 0 || val === 1) return val as AudioPreferenceValue;
        return 0; // default Balanced
      } catch {
        return 0;
      }
    },
    async setPreference(value: AudioPreferenceValue): Promise<boolean> {
      try {
        await request.post('/user/userInfo/c/settings.json', {
          audio_preference: value,
        });
        this.preference = value;
        return true;
      } catch {
        return false;
      }
    },
    clear() {
      this.preference = null;
      this._fetchPromise = null;
    },
  },
});

export default useAudioPreferenceStore;
